import {
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { CardContainer } from 'finara-components';

import type { Mitigation, RiskLevel } from '@/types/riskAssessment';

interface MitigationRecommendationsProps {
  mitigations: Mitigation[];
  overallRiskLevel: RiskLevel;
}

export function MitigationRecommendations({
  mitigations,
  overallRiskLevel,
}: MitigationRecommendationsProps) {
  const getPriorityIcon = (priority: Mitigation['priority']) => {
    switch (priority) {
      case 'high':
        return <ErrorOutline className="text-red-400" />;
      case 'medium':
        return <WarningAmberOutlined className="text-yellow-400" />;
      case 'low':
        return <InfoOutlined className="text-brand-blue" />;
    }
  };

  const getPriorityColor = (priority: Mitigation['priority']) => {
    switch (priority) {
      case 'high':
        return 'border-red-500/30 bg-red-500/10';
      case 'medium':
        return 'border-yellow-500/30 bg-yellow-500/10';
      case 'low':
        return 'border-blue-500/30 bg-blue-500/10';
    }
  };

  const getPriorityLabel = (priority: Mitigation['priority']) => {
    switch (priority) {
      case 'high':
        return 'Alta Prioridad';
      case 'medium':
        return 'Prioridad Media';
      case 'low':
        return 'Prioridad Baja';
    }
  };

  const getRiskLevelMessage = () => {
    switch (overallRiskLevel) {
      case 'low':
        return 'El cliente presenta un perfil de bajo riesgo. Se recomienda aplicar procedimientos estándar de debida diligencia.';
      case 'medium':
        return 'El cliente presenta un perfil de riesgo medio. Se recomienda implementar controles adicionales y monitoreo periódico.';
      case 'high':
        return 'El cliente presenta un perfil de alto riesgo. Se requiere implementar debida diligencia reforzada y controles estrictos.';
    }
  };

  return (
    <CardContainer>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <CheckCircleOutline className="text-brand-blue text-2xl" />
          <h2 className="text-xl font-bold text-brand-white">Recomendaciones de Mitigación</h2>
        </div>

        {/* Risk Level Message */}
        <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-sm text-muted">{getRiskLevelMessage()}</p>
        </div>

        {/* Mitigations List */}
        <div className="space-y-4">
          {mitigations.map(mitigation => (
            <div
              key={mitigation.id}
              className={`p-4 rounded-lg border-2 ${getPriorityColor(mitigation.priority)}`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">{getPriorityIcon(mitigation.priority)}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-brand-white">{mitigation.title}</h3>
                    <span
                      className={`text-xs px-2 py-1 rounded font-medium ${
                        mitigation.priority === 'high'
                          ? 'bg-red-500/20 text-red-400'
                          : mitigation.priority === 'medium'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-blue-500/20 text-brand-blue'
                      }`}
                    >
                      {getPriorityLabel(mitigation.priority)}
                    </span>
                  </div>
                  <p className="text-sm text-muted">{mitigation.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="text-sm font-semibold text-brand-white mb-3">Información Adicional</h4>
          <ul className="space-y-2 text-sm text-muted">
            <li className="flex items-start gap-2">
              <span className="text-brand-blue mt-0.5">•</span>
              <span>
                Las medidas de mitigación deben implementarse de acuerdo con el nivel de riesgo
                identificado.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-brand-blue mt-0.5">•</span>
              <span>
                Se recomienda documentar todas las acciones tomadas para mitigar los riesgos
                identificados.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-brand-blue mt-0.5">•</span>
              <span>
                La evaluación de riesgos debe revisarse periódicamente o cuando cambien las
                circunstancias del cliente.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-brand-blue mt-0.5">•</span>
              <span>
                Consulte con el oficial de cumplimiento para casos de alto riesgo o situaciones
                inusuales.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </CardContainer>
  );
}
